/**
 * The Bluetooth link to a NIIMBOT label printer.
 *
 * niimbluelib knows the NIIMBOT protocol - packets, checksums, print tasks, the
 * per-model differences between a B1 and a B21 - and leaves the transport
 * abstract. Four methods: connect, disconnect, isConnected, sendRaw. Everything
 * else is theirs.
 *
 * It ships a Capacitor transport of its own, which we cannot use: it is built
 * against @capacitor-community/bluetooth-le v8, which requires Capacitor 8, and
 * this app is on Capacitor 7. The BLE calls involved are identical across those
 * versions, so this is their implementation rewritten against the version we
 * run, with two changes noted below.
 *
 * Imports reach into dist rather than the package root on purpose. The root
 * index is CommonJS and eagerly requires all four transports including theirs,
 * which would pull a second copy of @capacitor/core and the v8 BLE plugin into
 * the bundle. Two Capacitor cores in one bundle is two plugin registries.
 */

import { BleClient } from '@capacitor-community/bluetooth-le';
import { NiimbotAbstractClient } from '@mmote/niimbluelib/dist/cjs/client/abstract_client';
import { ConnectResult } from '@mmote/niimbluelib/dist/cjs/packets';
import { ConnectEvent, DisconnectEvent, RawPacketSentEvent } from '@mmote/niimbluelib/dist/cjs/events';

/**
 * Wait for the Bluetooth adapter to admit it is on.
 *
 * @param {number} timeoutMs how long to keep asking before believing the answer
 */
async function waitForRadio(timeoutMs = 3000) {
  const deadline = Date.now() + timeoutMs;
  for (;;) {
    // eslint-disable-next-line no-await-in-loop
    if (await BleClient.isEnabled().catch(() => false)) return true;
    if (Date.now() >= deadline) return false;
    // eslint-disable-next-line no-await-in-loop
    await new Promise((r) => setTimeout(r, 150));
  }
}

/** A NIIMBOT talks on whichever characteristic can both notify and be written. */
async function findChannel(deviceId) {
  const services = await BleClient.getServices(deviceId);
  for (const service of services) {
    // The 16-bit generic services are not it; a printer's own channel is a full
    // vendor uuid. This is niimbluelib's own rule and it holds on the B1.
    if (service.uuid.length < 5) continue;
    for (const ch of service.characteristics || []) {
      if (ch.properties?.notify && ch.properties?.writeWithoutResponse) {
        return { service: service.uuid, characteristic: ch.uuid };
      }
    }
  }
  throw new Error('That device does not look like a label printer.');
}

export default class CardChaseNiimbotClient extends NiimbotAbstractClient {
  constructor() {
    super();
    this.deviceId = undefined;
    this.serviceUUID = undefined;
    this.characteristicUUID = undefined;
  }

  /**
   * @param {object} [options]
   * @param {string} [options.deviceId] reconnect to a printer already chosen.
   *   Without it the operating system's own picker opens, which is the only
   *   way a user is allowed to choose a device on iOS.
   */
  async connect(options = {}) {
    await this.disconnect();

    // initialize throws on iOS when the app has been refused Bluetooth, which
    // is a different problem from the radio being off and has a different fix.
    try {
      await BleClient.initialize({ androidNeverForLocation: true });
    } catch (err) {
      throw new Error(
        'CardChase has not been allowed to use Bluetooth. '
        + 'Open Settings, find CardChase, and turn Bluetooth on.',
      );
    }

    // CoreBluetooth does not know whether the radio is on at the moment it is
    // created. The manager starts in `unknown` and reports `poweredOn` a short
    // time later, so asking the instant initialize returns gets `false` on a
    // phone whose Bluetooth is plainly on and whose permission is granted -
    // which is exactly what happened on the first device this ever ran on.
    //
    // So it is asked repeatedly for a moment rather than once. Still false
    // after that and it really is off, or refused.
    if (!(await waitForRadio())) {
      throw new Error(
        'Bluetooth is off, or CardChase has not been allowed to use it. '
        + 'Check Settings, then CardChase, then Bluetooth.',
      );
    }

    // Unfiltered on purpose. These printers do not put their service in the
    // advertisement and most do not broadcast a name either, so filtering on
    // either one empties the list and leaves no way to connect at all. A noisy
    // picker is worse to look at and better than an impossible one.
    const device = options.deviceId
      ? { deviceId: options.deviceId, name: options.deviceName || options.deviceId }
      : await BleClient.requestDevice();

    await BleClient.connect(device.deviceId, () => this.handleDrop());
    await BleClient.discoverServices(device.deviceId);

    // Their version calls the disconnect handler in a .finally here, which runs
    // on success too and clears the device id a line before it is set. It works
    // by ordering rather than by intent. This only cleans up on failure.
    let channel;
    try {
      channel = await findChannel(device.deviceId);
    } catch (err) {
      await BleClient.disconnect(device.deviceId).catch(() => {});
      this.handleDrop();
      throw err;
    }

    this.deviceId = device.deviceId;
    this.serviceUUID = channel.service;
    this.characteristicUUID = channel.characteristic;

    await BleClient.startNotifications(
      this.deviceId, this.serviceUUID, this.characteristicUUID,
      (value) => this.processRawPacket(value),
    );

    await this.negotiateAndGetPrinterInfo(() => this.disconnect());

    const result = {
      deviceName: device.name,
      deviceId: device.deviceId,
      result: this.info.connectResult ?? ConnectResult.FirmwareErrors,
    };
    this.emit('connect', new ConnectEvent(result));
    return result;
  }

  handleDrop() {
    this.deviceId = undefined;
    this.serviceUUID = undefined;
    this.characteristicUUID = undefined;
    this.info = {};
    this.emit('disconnect', new DisconnectEvent());
  }

  isConnected() {
    return this.deviceId !== undefined;
  }

  async disconnect() {
    this.stopHeartbeat();
    if (this.deviceId !== undefined) {
      // Best effort. A printer switched off mid-job fails both of these, and a
      // throw here would leave the client believing it is still connected.
      await BleClient.stopNotifications(this.deviceId, this.serviceUUID, this.characteristicUUID).catch(() => {});
      await BleClient.disconnect(this.deviceId).catch(() => {});
    }
    this.deviceId = undefined;
    this.serviceUUID = undefined;
    this.characteristicUUID = undefined;
    this.info = {};
  }

  async sendRaw(data, force) {
    const send = async () => {
      if (!this.isConnected()) throw new Error('The printer is not connected.');
      // The pacing is the library's, and it is load bearing: these printers
      // drop bytes when written to flat out.
      if (this.packetIntervalMs > 0) {
        await new Promise((r) => setTimeout(r, this.packetIntervalMs));
      }
      const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
      await BleClient.writeWithoutResponse(
        this.deviceId, this.serviceUUID, this.characteristicUUID, view,
      );
      this.emit('rawpacketsent', new RawPacketSentEvent(data));
    };
    if (force) return send();
    return this.mutex.runExclusive(send);
  }

  // eslint-disable-next-line class-methods-use-this
  getType() {
    return 'capacitor-ble';
  }
}
